import { AddedAttribute } from 'pages/UserPage/components/MiddlePane/types';
import { DERIVED_ATTRIBUTE_REGEX } from 'common/constants/constants';
import { AttributeType, Section } from 'common/constants/types';

export const isStringDerivedAttribute = (value: string | number): boolean =>
  !!(value && typeof value === 'string' && value.match(DERIVED_ATTRIBUTE_REGEX));

export const isDerivedAttribute = (attribute: AddedAttribute): boolean => {
  if (attribute.is_derived) {
    return true;
  }
  return isStringDerivedAttribute(attribute.value);
};

export const isEquivalentAttribute = (attribute1: AddedAttribute, attribute2: AddedAttribute): boolean =>
  attribute1.value === attribute2.value
  && attribute1.is_derived === attribute2.is_derived
  && attribute1.is_secondary === attribute2.is_secondary;

/**
 * Converts an attribute to a display text.
 *
 * For non-derived attributes, this is just the attribute value.
 * For derived attributes, this is the attribute value surrounded by "--".
 */
export const parseAttributeToDisplayText = (attribute: AddedAttribute): string => (isDerivedAttribute(attribute) ? `--${attribute.value}--` : `${attribute.value}`);

export const parseAttributesToDisplayTexts = (attributes: AddedAttribute[]): string[] =>
  attributes.map(parseAttributeToDisplayText);

/**
 * Converts a display text back to an attribute.
 */
export const parseDisplayTextToAttribute = (displayText: string): AddedAttribute =>
{
  const isDerived = isStringDerivedAttribute(displayText);
  return {
    // this only works as long as we prefix and suffix derived attributes with --
    value: isDerived ? displayText.substring(2, displayText.length - 2) : displayText,
    is_derived: isDerived,
  };
};

export const parseDisplayTextsToAttributes = (displayTexts: string[]): AddedAttribute[] =>
  displayTexts && displayTexts.map(
    displayText => parseDisplayTextToAttribute(displayText)
  );

/**
 * Converts an attribute to a display value, which is a stringified version of the attribute.
 */
export const parseAttributeToDisplayValue = (attribute: AddedAttribute): string => {
  const { value, is_derived, is_secondary } = attribute;

  const displayValue = {
    value,
    is_derived,
    is_secondary,
  };

  return JSON.stringify(displayValue);
};

export const parseAttributesToDisplayValues = (attributes: AddedAttribute[]): string[] =>
  attributes.map(parseAttributeToDisplayValue);

/**
 * Converts a display value back to an attribute.
 */
export const parseDisplayValueToAttribute = (displayValue: string): AddedAttribute =>
    JSON.parse(displayValue) as AddedAttribute;

export const parseDisplayValuesToAttributes = (displayValues: string[]): AddedAttribute[] =>
  displayValues && displayValues.map(
    displayValue => parseDisplayValueToAttribute(displayValue)
  );

export const parseSectionToAttributeNameAndValues = (sections: Section[]): AttributeType[] =>
  sections.flatMap(section => section.attribute_types);

export const dedupeAttributes = (attributeType: AttributeType) => {
  const dedupedAttributeType = { ...attributeType };
  dedupedAttributeType.attributes = dedupedAttributeType.attributes.filter((attribute, ind) => ind
    === dedupedAttributeType.attributes.findIndex(innerAttribute => attribute.value === innerAttribute.value
      && attribute.start_date === innerAttribute.start_date
      && attribute.end_date === innerAttribute.end_date
      && attribute.is_derived === innerAttribute.is_derived
      && attribute.is_secondary === innerAttribute.is_secondary
    ));
  return dedupedAttributeType;
};
